/**
 * PDP shipping claim + pop-up, for the shopper's location.
 *
 * snippets/pdp-trust-bar.liquid renders a draft claim (the signed-in
 * customer's default address, else the options most of the country gets) and
 * inlines, in each shipping item, only what this variant needs:
 * [data-delivery-data] = {variant, price, at, policy, fallback, mirror
 * (its clock), plan, origin, class, snapshot, rules}. This re-words the claim
 * for window.BirchShipTo's location with assets/delivery-core.js and fills the
 * pop-up ([data-shipping-panel]) with every option checkout would offer:
 * name · price · timing, the processing time and cutoff, the product's
 * Shipping Rules, the states it doesn't ship to.
 *
 * The claim is re-worded only in the default case — a merchant's shipping
 * policy text (variant / product / OM rule / OM default) stays the claim and
 * heads the pop-up instead. No mirror → the server's text stays; no location →
 * the server's draft option ([data-delivery-data].draft), re-dated on today.
 *
 * Live check (OM /public/delivery-options): checkout's exact answer for one
 * ZIP, for carrier (Collective) plans and products with a hide rule. Asked
 * ONLY when the shopper opens the pop-up — the carrier path behind it is
 * throttled by Shopify, so it is never prefetched. Memoised per
 * variant|zip|po|day in memory and sessionStorage; until it answers (or when
 * it can't) the snapshot / zone options stay on screen. Its answer replaces
 * the pop-up's options and, when the cheapest differs, the claim.
 *
 * Re-renders on: load, `birch:shipping-claim-updated` (variant switch),
 * `birch:ship-to-changed`, `birch:shipping-panel-open`, and a tab coming back
 * into view (the clock may have passed the cutoff or midnight).
 */
(function () {
  'use strict';

  var D = window.BirchDelivery;
  if (!D || window.__birchPdpDelivery) return;
  window.__birchPdpDelivery = true;

  // A claim longer than this takes a row of its own — the same threshold the
  // trust bar's Liquid applies to its draft.
  var ROW_AT = 40;

  var PROD_OM = 'https://order-management.api.birchstore.com/public/delivery-options';
  var STAGING_OM = 'https://order-management.api-staging.birchstore.com/public/delivery-options';
  var NON_PROD_HOST_RX = /(^|\.)birch-staging\.myshopify\.com$|^(localhost|127\.0\.0\.1|.*\.local)$/i;
  var LIVE_KEY = 'birch:delivery-live';
  var LIVE_MAX = 40;
  var LIVE_TIMEOUT_MS = 10000;

  function liveUrl() {
    if (window.Theme && window.Theme.deliveryOptionsUrl) return window.Theme.deliveryOptionsUrl;
    return NON_PROD_HOST_RX.test(window.location.hostname) ? STAGING_OM : PROD_OM;
  }

  function shipTo() {
    return window.BirchShipTo ? window.BirchShipTo.get() : null;
  }

  /** The item's inline data, parsed once per text (a morph can rewrite it in place). */
  function readData(item) {
    var el = item.querySelector('[data-delivery-data]');
    if (!el) return null;
    var text = el.textContent;
    if (el.__birchText !== text) {
      el.__birchText = text;
      try {
        el.__birchData = JSON.parse(text);
      } catch (_err) {
        el.__birchData = null;
      }
    }
    return el.__birchData;
  }

  function coreInput(d, live) {
    return {
      mirror: d.mirror,
      plan: d.plan,
      origin: d.origin,
      at: d.at || null,
      snapshot: d.snapshot,
      productSnapshot: d.productSnapshot || null,
      price: d.price,
      rules: d.rules,
      live: live || null
    };
  }

  // ── Live check ─────────────────────────────────────────────────────────────

  var memo = {}; // key → {day, options, blocked} | false (unavailable on this page)
  var inflight = {};

  function readSession() {
    try {
      return JSON.parse(window.sessionStorage.getItem(LIVE_KEY)) || {};
    } catch (_err) {
      return {};
    }
  }

  function liveFor(key) {
    if (Object.prototype.hasOwnProperty.call(memo, key)) return memo[key] || null;
    var hit = readSession()[key];
    if (hit && Array.isArray(hit.options)) {
      memo[key] = hit;
      return hit;
    }
    return null;
  }

  function saveLive(key, value) {
    memo[key] = value;
    try {
      var all = readSession();
      all[key] = value;
      var keys = Object.keys(all);
      while (keys.length > LIVE_MAX) delete all[keys.shift()];
      window.sessionStorage.setItem(LIVE_KEY, JSON.stringify(all));
    } catch (_err) {
      /* memory only, then */
    }
  }

  /** Worth asking: a carrier plan or a hide rule, and a ZIP to ask about. */
  function liveKey(d, loc) {
    if (!d || !d.plan || !d.mirror || !d.variant || !loc || !loc.zip) return null;
    if (!d.plan.carrier && !D.hasHideRule(d.rules)) return null;
    var day = D.zonedNow(Date.now(), d.mirror.timeZone).day;
    return d.variant + '|' + loc.zip + '|' + (loc.po ? 1 : 0) + '|' + day;
  }

  function askLive(key, d, loc) {
    if (Object.prototype.hasOwnProperty.call(memo, key) || inflight[key] || !window.fetch) return;
    inflight[key] = true;
    var url = liveUrl() + '?variant=' + encodeURIComponent(d.variant) + '&zip=' + encodeURIComponent(loc.zip) + '&po=' + (loc.po ? 1 : 0);
    var ctrl = window.AbortController ? new AbortController() : null;
    var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, LIVE_TIMEOUT_MS) : null;
    fetch(url, { credentials: 'omit', signal: ctrl ? ctrl.signal : undefined })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (res) {
        if (res && res.ok === true && Array.isArray(res.options)) {
          saveLive(key, { day: String(res.day || ''), options: res.options, blocked: res.blocked || null });
        } else {
          // invalid_zip / not_found / unavailable / rate_limited: keep what's
          // shown, and don't ask again from this page.
          memo[key] = false;
        }
      })
      .catch(function () {
        memo[key] = false;
      })
      .then(function () {
        if (timer) clearTimeout(timer);
        delete inflight[key];
        renderAll();
      });
  }

  // ── Claim ──────────────────────────────────────────────────────────────────

  function setClaim(item, text) {
    var label = item.querySelector('[data-shipping-label]');
    if (!label || !text || label.textContent === text) return;
    label.textContent = text;
    item.toggleAttribute('data-shipping-row', text.length > ROW_AT);
    // The bar re-checks its dividers on resize; ResizeObserver catches a wrap
    // by itself, so only the legacy fallback needs the nudge.
    if (!window.ResizeObserver) window.dispatchEvent(new Event('resize'));
  }

  /**
   * No location: the server's draft option, worded on today — the page may be
   * a cached copy from before the cutoff or an earlier day. Same dating rules
   * as D.resolve.
   */
  function draftLine(d, now) {
    var mirror = d.mirror;
    var datable = mirror.datesEnabled === true && mirror.processingDays != null && (d.origin || 'own') === 'own';
    var fresh = d.draft.day != null && d.draft.day === D.zonedNow(now, mirror.timeZone).day;
    return D.lineText(D.describe(d.draft.option, mirror, datable, fresh, now, d.at || null));
  }

  // ── Pop-up ─────────────────────────────────────────────────────────────────

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }

  function list(names) {
    if (names.length < 2) return names.join('');
    return names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];
  }

  function notShippedNote(d) {
    var gaps = D.notShippedStates(coreInput(d, null));
    if (!gaps.length) return '';
    var all = Object.keys(D.US_STATES);
    if (gaps.length <= 12) return "Doesn't ship to " + list(gaps.map(D.stateName)) + '.';
    var ships = all.filter(function (c) { return gaps.indexOf(c) === -1; });
    if (!ships.length) return '';
    return ships.length <= 12 ? 'Ships only to ' + list(ships.map(D.stateName)) + '.' : "Doesn't ship to " + gaps.length + ' states.';
  }

  function renderPanel(bar, item, d, view, loc, now, checking) {
    var body = bar.querySelector('[data-shipping-panel]');
    if (!body) return;
    var frag = document.createDocumentFragment();
    var knowsPlan = !!(d.mirror && d.plan);

    if (knowsPlan) {
      var to = el('p', 'pdp-shipping-dialog__to');
      if (loc && loc.state) {
        to.appendChild(document.createTextNode('Shipping to '));
        to.appendChild(el('strong', null, window.BirchShipTo && window.BirchShipTo.place ? window.BirchShipTo.place(loc) : loc.zip || D.stateName(loc.state)));
        to.appendChild(document.createTextNode(' · '));
      } else {
        to.appendChild(document.createTextNode('Enter your ZIP code for exact options · '));
      }
      var change = el('button', 'pdp-shipping-dialog__change', loc && loc.state ? 'Change' : 'Set location');
      change.type = 'button';
      change.setAttribute('data-ship-to-open', '');
      change.setAttribute('aria-haspopup', 'dialog');
      to.appendChild(change);
      frag.appendChild(to);
    }

    // The merchant's own text (levels 1–4) heads the panel. Read from the claim
    // on screen: assets/shipping-delivery-dates.js keeps a dated one current.
    if (d.policy) {
      var label = item.querySelector('[data-shipping-label]');
      frag.appendChild(el('p', 'pdp-shipping-dialog__policy', (label && label.textContent.trim()) || d.policy));
    }

    if (view && view.blocked) {
      frag.appendChild(el('p', 'pdp-shipping-dialog__block', view.blocked)).setAttribute('data-applies', '');
    } else if (view && view.options.length) {
      var ul = el('ul', 'pdp-shipping-dialog__options');
      view.options.forEach(function (o) {
        var li = el('li', 'pdp-shipping-dialog__option');
        li.appendChild(el('span', 'pdp-shipping-dialog__option-name', o.name || 'Shipping'));
        li.appendChild(el('span', 'pdp-shipping-dialog__option-price', D.priceText(o.price)));
        var timing = o.delivery ? 'Arrives ' + o.delivery.when : o.words;
        if (timing) li.appendChild(el('span', 'pdp-shipping-dialog__option-timing', timing));
        ul.appendChild(li);
      });
      frag.appendChild(ul);
    } else if (view) {
      frag.appendChild(el('p', 'pdp-shipping-dialog__block', view.line)).setAttribute('data-applies', '');
    } else if (!d.policy) {
      // No location (or no mirror): only what is known — the claim as shown.
      var claim = item.querySelector('[data-shipping-label]');
      frag.appendChild(el('p', null, (claim && claim.textContent.trim()) || d.fallback || 'Free Shipping'));
    }

    if (checking) frag.appendChild(el('p', 'pdp-shipping-dialog__note', 'Checking the options at checkout for ' + loc.zip + '…'));

    // Every Shipping Rule message the product carries, once — the one that
    // stops this address is already shown above.
    var blocks = view ? view.blocks : knowsPlan ? D.blockRules(d.rules, {}) : [];
    blocks.forEach(function (b) {
      if (view && view.blocked === b.message) return;
      frag.appendChild(el('p', 'pdp-shipping-dialog__note', b.message));
    });

    if (knowsPlan) {
      var gaps = notShippedNote(d);
      if (gaps) frag.appendChild(el('p', 'pdp-shipping-dialog__note', gaps));
    }

    body.toggleAttribute('aria-busy', !!checking);
    if (body.replaceChildren) body.replaceChildren(frag);
    else {
      body.textContent = '';
      body.appendChild(frag);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  /** One bar. `ask` = the shopper just opened its pop-up: ask the live check now. */
  function render(bar, ask) {
    var item = bar.querySelector('[data-shipping-item]');
    if (!item) return;
    var d = readData(item);
    if (!d) return;
    var loc = shipTo();
    var now = Date.now();
    var key = liveKey(d, loc);
    if (key && ask) askLive(key, d, loc);
    var live = key ? liveFor(key) : null;
    var view = d.mirror && d.plan ? D.resolve(coreInput(d, live), loc, now) : null;
    if (!d.policy && view) setClaim(item, view.line);
    else if (!d.policy && d.draft && d.mirror && d.plan) setClaim(item, draftLine(d, now));
    renderPanel(bar, item, d, view, loc, now, !!(key && inflight[key]));
  }

  function renderAll() {
    var bars = document.querySelectorAll('.pdp-trust-bar');
    for (var i = 0; i < bars.length; i++) render(bars[i], false);
  }

  renderAll();
  document.addEventListener('birch:shipping-claim-updated', renderAll);
  document.addEventListener('birch:ship-to-changed', function () {
    renderAll();
    // A new location while the pop-up is open is a fresh question for it.
    var open = document.querySelector('.pdp-trust-bar [data-shipping-dialog][open]');
    if (open) render(open.closest('.pdp-trust-bar'), true);
  });
  document.addEventListener('birch:shipping-panel-open', function (event) {
    var bar = event.detail && event.detail.bar;
    if (bar) render(bar, true);
  });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') renderAll();
  });
})();
